/**
 * RealTimeModeView — sidebar surface for the Real-Time Mode match state.
 *
 * Layout (matched / likely):
 *
 *   ┌─ Sticky header ─────────────────────────────────┐
 *   │  [icon] Live Patient Snapshot              [X]  │
 *   │         ModMed · real-time                       │
 *   │         Gabriel Belcher                          │
 *   │         DOB 02/25/1983 · MRN 925897175           │
 *   │         [99% match]  matched by MRN + DOB        │
 *   ├─ Scrollable body (max-h 220px) ─────────────────┤
 *   │  Balance metric · issues insight · recent claims │
 *   ├─ Sticky action footer ──────────────────────────┤
 *   │  [chip][chip] · [Use ModMed Specialist][Attach] │
 *   └─────────────────────────────────────────────────┘
 *
 * Why the 3-zone split: previously every section (patient, balance, claims,
 * issues, actions) lived inside a single 220px-capped scroll region, which
 * meant "Attach to chat" — the bottom-most element — was always clipped
 * below the fold. Pinning actions to a footer outside the scroll region
 * keeps the primary CTA permanently visible regardless of content height.
 *
 * Privacy rules baked in (PRD §6 + §10) — unchanged from the previous
 * implementation:
 *   - `unsafe` and `multiple` MUST NOT render financial data — they fall
 *     through to a sanitized candidate-only state.
 *   - `none` exposes the manual-search fallback (deep-link into Enter).
 *   - Match confidence + basis are surfaced for matched/likely.
 */

import {
  Activity,
  AlertTriangle,
  CalendarClock,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  ChevronUp,
  CreditCard,
  ExternalLink,
  FileText,
  Info,
  ListChecks,
  Loader2,
  Paperclip,
  Plug,
  Sparkles,
  User,
  X,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import type {
  ClaimStatus,
  CurrentIssue,
  EnterPatient,
  EnterTask,
  IssueSeverity,
  MatchBasis,
  MatchState,
  RealTimeModeData,
  RecentClaim,
  RecommendedAction,
} from '@ctrl-enter/types';
import { providerDisplayName } from '@ctrl-enter/types';
import {
  useRealTimeModeMatch,
  useRealTimeModePickedCandidate,
  useRealTimeModeReset,
  useRealTimeModeSuggestedMcpAccountId,
} from '../stores/real-time-mode-store';
import { useResolveRealTimeModeMutation } from '../hooks/use-real-time-mode-resolve';
import { usePatientTasksQuery } from '../hooks/use-gateway-queries';
import { EnterTasksOnboarding, useTasksOnboarding } from './enter-tasks-onboarding';
import { RecordPaymentSheet } from './record-payment-sheet';
import { cn } from '../utils/cn';

const MATCH_BASIS_LABEL: Record<MatchBasis, string> = {
  mrn: 'MRN',
  emr_account_id: 'Account ID',
  emr_patient_id: 'EMR Patient ID',
  dob: 'DOB',
  name: 'Name',
};

const CLAIM_STATUS_TONE: Record<ClaimStatus, string> = {
  paid: 'text-emerald-500',
  denied: 'text-destructive',
  rejected: 'text-destructive',
  submitted: 'text-primary',
  pending: 'text-amber-500',
  partial: 'text-amber-500',
  unknown: 'text-muted-foreground',
};

const ISSUE_SEVERITY_DOT: Record<IssueSeverity, string> = {
  high: 'bg-destructive',
  medium: 'bg-amber-500',
  low: 'bg-muted-foreground',
};

const ISSUE_SEVERITY_SURFACE: Record<IssueSeverity, string> = {
  high: 'border-destructive/30 bg-destructive/5',
  medium: 'border-amber-500/30 bg-amber-500/5',
  low: 'border-border bg-muted/30',
};

const TASK_SEVERITY_DOT: Record<EnterTask['severity'], string> = {
  urgent: 'bg-destructive',
  high: 'bg-amber-500',
  normal: 'bg-muted-foreground',
  low: 'bg-muted-foreground/40',
};

// Category labels come straight from Enter (data-driven, e.g. "Invalid Claim -
// Interface"), so assign a stable chip tone by hashing the name rather than
// hardcoding per-label colors.
const CATEGORY_TONES = [
  'bg-primary/15 text-primary',
  'bg-emerald-500/15 text-emerald-600',
  'bg-amber-500/15 text-amber-600',
  'bg-violet-500/15 text-violet-600',
  'bg-sky-500/15 text-sky-600',
  'bg-rose-500/15 text-rose-600',
];
function categoryTone(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return CATEGORY_TONES[h % CATEGORY_TONES.length] ?? 'bg-muted text-muted-foreground';
}

type RealTimeModeViewProps = {
  /**
   * Optional callback the user can fire to attach the current resolved
   * patient context into the chat composer. Only displayed when the
   * match is `matched` or `likely` (privacy: never expose financial
   * details from `unsafe` / `multiple` to the AI).
   */
  onAttachContext?: () => void;
  /**
   * Optional one-click action that BOTH selects the ModMed Specialist
   * agent for the current chat session AND attaches the resolved
   * context. Chat-view passes this only when (a) we have a matched /
   * likely state AND (b) the ModMed Specialist isn't already active —
   * the button is hidden otherwise.
   */
  onUseModmedSpecialist?: () => void;
  /**
   * Fired from the `no_integration` empty state — chat-view flips the
   * web app over to the Integrations settings panel so the user can
   * connect Enter without leaving the sidebar context. Without this
   * callback the empty state falls back to a plain external link.
   */
  onConnectIntegration?: () => void;
};

export function RealTimeModeView({
  onAttachContext,
  onUseModmedSpecialist,
  onConnectIntegration,
}: RealTimeModeViewProps = {}) {
  const match = useRealTimeModeMatch();
  const reset = useRealTimeModeReset();
  const [minimized, setMinimized] = useState(false);

  if (match.status === 'idle') return null;

  const isResolved = match.status === 'matched' || match.status === 'likely';
  const hasFooterActions =
    isResolved &&
    (Boolean(onAttachContext) ||
      Boolean(onUseModmedSpecialist) ||
      match.data.recommendedActions.length > 0);

  return (
    <div
      className={cn(
        'flex flex-col',
        'bg-card text-card-foreground text-sm',
        'border-b border-border shadow-sm',
        'animate-in slide-in-from-top-2 duration-200'
      )}
    >
      <SnapshotHeader
        match={match}
        onDismiss={reset}
        minimized={minimized}
        onToggleMinimize={() => setMinimized(v => !v)}
      />

      {!minimized && (
        <div className="overflow-y-auto max-h-[220px] px-4 py-2">
          <RealTimeModeBody
            match={match}
            onConnectIntegration={onConnectIntegration}
          />
        </div>
      )}

      {!minimized && hasFooterActions && (
        <SnapshotActionFooter
          actions={match.data.recommendedActions}
          enterPatientUrl={match.data.enterPatientUrl}
          {...(onAttachContext ? { onAttachContext } : {})}
          {...(onUseModmedSpecialist ? { onUseModmedSpecialist } : {})}
        />
      )}
    </div>
  );
}

// ─── Header ────────────────────────────────────────────────────────────────

function SnapshotHeader({
  match,
  onDismiss,
  minimized,
  onToggleMinimize,
}: {
  match: MatchState;
  onDismiss: () => void;
  minimized: boolean;
  onToggleMinimize: () => void;
}) {
  const isResolved = match.status === 'matched' || match.status === 'likely';

  return (
    <header className={cn('px-4 pt-3 pb-2', !minimized && 'border-b border-border/40')}>
      <div className="flex items-start gap-2.5">
        <div className="flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center bg-primary/15 text-primary">
          <Activity size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-[13px] font-semibold text-foreground leading-tight">
            Live Patient Snapshot
          </h2>
          <p className="text-[11px] text-muted-foreground leading-tight mt-0.5 truncate">
            {match.status !== 'idle' && match.context
              ? providerDisplayName(match.context.provider)
              : 'EMR'}{' '}
            · real-time
            {minimized && isResolved && (
              <> &mdash; <span data-phi className="text-foreground/80 font-medium">{match.data.patient.name}</span></>
            )}
          </p>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            type="button"
            onClick={onToggleMinimize}
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
            aria-label={minimized ? 'Expand' : 'Minimise'}
          >
            {minimized ? <ChevronsUpDown size={13} /> : <ChevronsDownUp size={13} />}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="text-muted-foreground hover:text-foreground transition-colors -mr-1 p-1 rounded"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {!minimized && isResolved && (
        <IdentitySummary
          patient={match.data.patient}
          confidence={match.confidence}
          basis={match.basis}
          status={match.status}
        />
      )}
    </header>
  );
}

function IdentitySummary({
  patient,
  confidence,
  basis,
  status,
}: {
  patient: EnterPatient;
  confidence: number;
  basis: MatchBasis[];
  status: 'matched' | 'likely';
}) {
  const confidenceLabel = status === 'matched' ? 'High confidence' : 'Likely match';
  const confidencePct = `${(confidence * 100).toFixed(0)}%`;
  const basisLabel = basis.map(b => MATCH_BASIS_LABEL[b]).join(' + ');

  const idParts: string[] = [];
  if (patient.dob) idParts.push(`DOB ${formatDob(patient.dob)}`);
  if (patient.mrn) idParts.push(`MRN ${patient.mrn}`);

  return (
    // data-phi: name + DOB + MRN — masked in Sentry Session Replay.
    <div data-phi className="mt-2 pl-[38px]">
      <p className="text-[14px] font-semibold text-foreground truncate leading-tight">
        {patient.name}
      </p>
      {idParts.length > 0 && (
        <p className="text-[11px] text-muted-foreground mt-0.5">
          {idParts.join(' · ')}
        </p>
      )}
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
        <ConfidencePill
          tone={status === 'matched' ? 'strong' : 'soft'}
          label={`${confidencePct} match`}
        />
        <span className="text-[11px] text-muted-foreground">
          {confidenceLabel}
          {basisLabel ? ` · matched by ${basisLabel}` : ''}
        </span>
      </div>
    </div>
  );
}

function ConfidencePill({
  label,
  tone,
}: {
  label: string;
  tone: 'strong' | 'soft';
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none',
        tone === 'strong'
          ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30'
          : 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30'
      )}
    >
      {label}
    </span>
  );
}

// ─── Body dispatcher ───────────────────────────────────────────────────────

function RealTimeModeBody({
  match,
  onConnectIntegration,
}: {
  match: MatchState;
  onConnectIntegration?: () => void;
}) {
  switch (match.status) {
    case 'loading':
      return (
        <div className="flex items-center gap-2 text-muted-foreground py-1">
          <span className="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
          <span>Resolving patient…</span>
        </div>
      );

    case 'matched':
    case 'likely':
      return (
        <ResolvedBody
          data={match.data}
          providerLabel={providerDisplayName(match.context.provider)}
        />
      );

    case 'multiple':
      return <MultiMatchPicker context={match.context} candidates={match.candidates} />;

    case 'unsafe':
      return (
        <UnsafeMatch
          candidates={match.candidates}
          observedNameOnly={!match.context.observedFields.dob && !match.context.observedFields.mrn}
        />
      );

    case 'none':
      return <NoMatch context={match.context} />;

    case 'no_integration':
      return <NoIntegration onConnectIntegration={onConnectIntegration} />;

    case 'error':
      return <p className="text-destructive">{match.error}</p>;

    default:
      return null;
  }
}

// ─── Resolved (matched / likely) body ──────────────────────────────────────

function ResolvedBody({
  data,
  providerLabel,
}: {
  data: RealTimeModeData;
  providerLabel: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <BalanceMetric data={data} providerLabel={providerLabel} />
      {data.emrPatientAging && <AgingChips aging={data.emrPatientAging} />}
      <RecordPaymentAction data={data} />
      {data.currentIssues.length > 0 && <IssuesInsight issues={data.currentIssues} />}
      {data.recentClaims.length > 0 && <RecentClaims claims={data.recentClaims} />}
      <PatientTasks claims={data.recentClaims} />
    </div>
  );
}

// Graduated aging chips (per the product design): the oldest money reads
// hottest. Tone is by bucket POSITION, not label text, so it works for any
// EMR's bucket split (CentralReach uses 5; others use 4).
const AGING_TONES = [
  'bg-emerald-500/15 text-emerald-600',
  'bg-amber-500/15 text-amber-600',
  'bg-orange-500/15 text-orange-600',
  'bg-destructive/15 text-destructive',
  'bg-destructive/25 text-destructive',
];
function agingTone(index: number, count: number): string {
  if (count <= 1) return AGING_TONES[0] as string;
  // Map this bucket onto the tone ramp regardless of how many buckets exist.
  const scaled = Math.round((index / (count - 1)) * (AGING_TONES.length - 1));
  return AGING_TONES[scaled] ?? (AGING_TONES[0] as string);
}

// A — Patient A/R aging, straight from the EMR (Enter exposes no aging data).
// Zero-only aging is noise, so the section hides itself when nothing is owed.
function AgingChips({ aging }: { aging: NonNullable<RealTimeModeData['emrPatientAging']> }) {
  const nonZero = aging.buckets.filter(b => b.amount !== 0);
  if (nonZero.length === 0) return null;

  return (
    <section className="rounded-lg border border-border bg-muted/20 px-3 py-2">
      <header className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <CalendarClock size={11} className="text-muted-foreground" />
          <span className="text-[12px] font-medium text-muted-foreground">Aging</span>
        </div>
        {aging.total !== undefined && (
          <span className="text-[11px] text-foreground tabular-nums">
            {formatCurrency(aging.total)}
          </span>
        )}
      </header>
      <div className="flex flex-wrap gap-1">
        {nonZero.map((bucket, i) => (
          <span
            key={bucket.label}
            className={cn(
              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] tabular-nums',
              agingTone(aging.buckets.indexOf(bucket), aging.buckets.length)
            )}
            title={`${bucket.label} days`}
          >
            <span className="opacity-70">{bucket.label}</span>
            {formatCurrency(bucket.amount)}
          </span>
        ))}
      </div>
    </section>
  );
}

// P — "Record Patient Payment" bar + drawer (per the product design: a
// full-width tinted action under the snapshot). Needs the matched Enter
// account to route the credential, so it hides when RTM didn't surface one.
function RecordPaymentAction({ data }: { data: RealTimeModeData }) {
  // The Enter account RTM matched this patient in — needed to route the
  // credential when posting. RTM stores it as the suggested MCP account.
  const matchedAccountId = useRealTimeModeSuggestedMcpAccountId();
  const [open, setOpen] = useState(false);
  if (!matchedAccountId) return null;

  // Apply to the most recent claim when there is one; otherwise record an
  // unapplied patient payment.
  const claimId = data.recentClaims[0]?.claimId;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'w-full flex items-center justify-center gap-1.5 py-2 rounded-lg',
          'text-[12px] font-medium bg-primary/10 text-primary hover:bg-primary/15 transition-colors'
        )}
      >
        <CreditCard size={14} /> Record Patient Payment
      </button>
      <RecordPaymentSheet
        open={open}
        onClose={() => setOpen(false)}
        accountId={matchedAccountId}
        {...(claimId ? { claimId } : {})}
        {...(data.patient?.name ? { patientName: data.patient.name } : {})}
      />
    </>
  );
}

// B — Balance metric card (compact, value-first)
function BalanceMetric({
  data,
  providerLabel,
}: {
  data: RealTimeModeData;
  providerLabel: string;
}) {
  const { financialSummary, discrepancy } = data;
  const patientBalance = financialSummary.outstandingPatientBalance;
  const hasPatientBalance = patientBalance !== null && patientBalance > 0;

  return (
    <section className="rounded-lg border border-border bg-muted/20 px-3 py-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[12px] font-medium text-muted-foreground">
          Outstanding balance
        </span>
        <span className="text-[15px] font-semibold text-foreground tabular-nums">
          {patientBalance === null ? '—' : formatCurrency(patientBalance)}
        </span>
      </div>
      {hasPatientBalance && (
        <p className="text-[10px] text-muted-foreground mt-0.5">
          Patient responsibility detected
        </p>
      )}

      <div className="mt-2 pt-1.5 border-t border-border/40 grid grid-cols-3 gap-1.5 text-[11px]">
        <MetricCell
          label="Patient"
          value={financialSummary.outstandingPatientBalance}
          emphasis
        />
        {financialSummary.outstandingInsuranceBalance !== undefined ? (
          <MetricCell
            label="Insurance"
            value={financialSummary.outstandingInsuranceBalance}
          />
        ) : (
          <div />
        )}
        {financialSummary.totalAr !== undefined ? (
          <MetricCell label="Total AR" value={financialSummary.totalAr} />
        ) : (
          <div />
        )}
      </div>

      {discrepancy && (
        <DiscrepancyChip discrepancy={discrepancy} providerLabel={providerLabel} />
      )}

      {financialSummary.lastUpdated && (
        <p className="text-[10px] text-muted-foreground/80 mt-1.5">
          Updated {formatTimestamp(financialSummary.lastUpdated)}
        </p>
      )}
    </section>
  );
}

function MetricCell({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: number | null;
  emphasis?: boolean;
}) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] text-muted-foreground leading-tight">{label}</span>
      <span
        className={cn(
          'tabular-nums leading-tight mt-0.5',
          emphasis ? 'font-semibold text-foreground' : 'text-foreground/85'
        )}
      >
        {value === null ? '—' : formatCurrency(value)}
      </span>
    </div>
  );
}

/**
 * Discrepancy chip — surfaces a mismatch between Enter's authoritative
 * balance and the EMR-visible balance scraped from the patient header.
 *
 * Reading: `deltaCents > 0` means Enter > EMR (EMR undercounting);
 * `deltaCents < 0` means Enter < EMR (EMR overcounting). The chip
 * displays the absolute delta with a direction arrow + both source
 * values for context.
 */
function DiscrepancyChip({
  discrepancy,
  providerLabel,
}: {
  discrepancy: NonNullable<RealTimeModeData['discrepancy']>;
  providerLabel: string;
}) {
  const { deltaCents, enterBalance, emrBalance } = discrepancy;
  const delta = Math.abs(deltaCents / 100);
  const direction = deltaCents > 0 ? 'higher than' : 'lower than';
  return (
    <div className="mt-1.5 flex items-start gap-1.5 px-2 py-1.5 rounded-md border border-amber-500/30 bg-amber-500/10">
      <AlertTriangle size={11} className="text-amber-500 mt-0.5 flex-shrink-0" />
      <div className="space-y-0.5 text-[11px]">
        <p className="text-foreground font-medium leading-tight">
          Enter balance is {direction} {providerLabel} by {formatCurrency(delta)}
        </p>
        <p className="text-muted-foreground leading-tight">
          Enter: {formatCurrency(enterBalance)} · {providerLabel}:{' '}
          {formatCurrency(emrBalance)}
        </p>
      </div>
    </div>
  );
}

// D — Current issues as insight cards
function IssuesInsight({ issues }: { issues: CurrentIssue[] }) {
  return (
    <div className="flex flex-col gap-1.5">
      {issues.map((issue, idx) => (
        <article
          key={`${issue.type}-${idx}`}
          className={cn(
            'flex items-start gap-2 px-2.5 py-1.5 rounded-lg border',
            ISSUE_SEVERITY_SURFACE[issue.severity]
          )}
        >
          <span
            className={cn(
              'mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0',
              ISSUE_SEVERITY_DOT[issue.severity]
            )}
          />
          <div className="min-w-0">
            <p className="text-[12px] font-medium text-foreground leading-tight">
              {issueHeadline(issue)}
            </p>
            <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
              {issue.message}
            </p>
          </div>
        </article>
      ))}
    </div>
  );
}

function issueHeadline(issue: CurrentIssue): string {
  switch (issue.type) {
    case 'patient_balance':
      return 'Outstanding balance detected';
    case 'open_denial':
      return 'Open claim denial';
    default:
      return capitalize(issue.severity) + ' priority';
  }
}

// C — Recent claims (compact, collapsible — 2 visible by default)
function RecentClaims({ claims }: { claims: RecentClaim[] }) {
  const COLLAPSED_COUNT = 2;
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? claims.slice(0, 5) : claims.slice(0, COLLAPSED_COUNT);
  const remaining = Math.min(claims.length, 5) - visible.length;

  return (
    <section className="rounded-lg border border-border bg-muted/20 px-3 py-2">
      <header className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <FileText size={11} className="text-muted-foreground" />
          <span className="text-[12px] font-medium text-muted-foreground">
            Recent claims
          </span>
        </div>
        {claims.length > COLLAPSED_COUNT && (
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
          >
            {expanded ? (
              <>
                Show less <ChevronUp size={10} />
              </>
            ) : (
              <>
                Show {remaining > 0 ? remaining : claims.length - visible.length} more <ChevronDown size={10} />
              </>
            )}
          </button>
        )}
      </header>
      <ul className="space-y-1">
        {visible.map(claim => (
          <li
            key={claim.claimId}
            className="text-[11px] flex flex-wrap items-baseline gap-x-2 leading-tight"
          >
            {claim.dateOfService && (
              <span className="text-muted-foreground">{formatDate(claim.dateOfService)}</span>
            )}
            {claim.payer && <span className="text-foreground/85">{claim.payer}</span>}
            <span className={cn('font-medium', CLAIM_STATUS_TONE[claim.status])}>
              {capitalize(claim.status)}
            </span>
            {claim.patientResponsibility !== null &&
              claim.patientResponsibility !== undefined && (
                <span className="text-muted-foreground tabular-nums">
                  PR {formatCurrency(claim.patientResponsibility)}
                </span>
              )}
          </li>
        ))}
      </ul>
    </section>
  );
}

// P — Patient-scoped Open Tasks (the RCM worklist for THIS patient's claims).
// Unlike the sidebar worklist (user-scoped), this shows every assignee's open
// task for the patient, enriched with category + assignee names.
function PatientTasks({ claims }: { claims: RecentClaim[] }) {
  const claimIds = claims.map(c => c.claimId).filter(Boolean);
  // Scope to the account RTM matched this patient in — the same one the payment
  // action routes through. Falls back to `undefined` (all connected accounts)
  // when RTM surfaced none: claim ids are globally unique, so the fan-out still
  // returns only the owning practice's rows, and narrowing to nothing here
  // would hide tasks that do belong to this patient.
  const matchedAccountId = useRealTimeModeSuggestedMcpAccountId();
  const query = usePatientTasksQuery(claimIds, true, matchedAccountId ?? undefined);
  const onboarding = useTasksOnboarding(false); // ⓘ re-show only — no auto-popup in the snapshot
  const tasks: EnterTask[] = query.data?.status === 200 ? query.data.body.tasks : [];

  // Nothing to scope by, or (once loaded) no tasks / an error → hide the section
  // entirely to keep the dense snapshot uncluttered (same pattern as claims/issues).
  if (claimIds.length === 0) return null;
  if (!query.isLoading && tasks.length === 0) return null;

  return (
    <section className="rounded-lg border border-border bg-muted/20 px-3 py-2">
      <header className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <ListChecks size={11} className="text-muted-foreground" />
          <span className="text-[12px] font-medium text-muted-foreground">Open tasks</span>
          {tasks.length > 0 && (
            <span className="text-[11px] text-muted-foreground">{tasks.length}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/80">
            <Zap size={10} /> auto-detected
          </span>
          <button
            type="button"
            onClick={onboarding.show}
            aria-label="How Tasks work"
            title="How Tasks work"
            className="p-0.5 rounded text-muted-foreground/70 hover:text-foreground transition-colors"
          >
            <Info size={11} />
          </button>
        </div>
      </header>

      {query.isLoading ? (
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground py-0.5">
          <Loader2 size={11} className="animate-spin" /> Loading tasks…
        </div>
      ) : (
        <ul className="space-y-1.5">
          {tasks.map(task => (
            <PatientTaskRow key={task.id} task={task} />
          ))}
        </ul>
      )}

      <EnterTasksOnboarding open={onboarding.open} onDismiss={onboarding.dismiss} />
    </section>
  );
}

function PatientTaskRow({ task }: { task: EnterTask }) {
  return (
    <li className="flex items-start gap-2 text-[11px] leading-tight">
      <span
        className={cn(
          'mt-1 inline-block w-1.5 h-1.5 rounded-full shrink-0',
          TASK_SEVERITY_DOT[task.severity]
        )}
        role="img"
        aria-label={`${task.severity} priority`}
        title={`${task.severity} priority`}
      />
      <div className="flex-1 min-w-0">
        <p className="text-foreground/90">{task.title}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-foreground">
          {task.categoryName && (
            <span className={cn('px-1.5 py-px rounded-full', categoryTone(task.categoryName))}>
              {task.categoryName}
            </span>
          )}
          {task.claimId && (
            <span className="inline-flex items-center gap-0.5">
              <FileText size={9} /> {task.claimId.slice(0, 8)}
            </span>
          )}
          {task.assigneeName && (
            <span className="inline-flex items-center gap-0.5">
              <User size={9} /> {task.assigneeName}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

// ─── Action footer (pinned — always visible) ───────────────────────────────

function SnapshotActionFooter({
  actions,
  enterPatientUrl,
  onAttachContext,
  onUseModmedSpecialist,
}: {
  actions: RecommendedAction[];
  enterPatientUrl?: string;
  onAttachContext?: () => void;
  onUseModmedSpecialist?: () => void;
}) {
  return (
    <footer className="px-4 py-2 border-t border-border/40 bg-card/40">
      <div className="flex flex-wrap items-center gap-1.5">
        {actions.map((action, idx) => {
          const href = action.href ?? enterPatientUrl;
          return (
            <ActionChip
              key={`${action.type}-${idx}`}
              label={action.label}
              tone="secondary"
              {...(href ? { href } : {})}
            />
          );
        })}
        {onUseModmedSpecialist && (
          <ActionChip
            label="Use ModMed Specialist"
            tone="primary"
            icon={<Sparkles size={11} />}
            onClick={onUseModmedSpecialist}
          />
        )}
        {onAttachContext && (
          <ActionChip
            label="Attach to chat"
            tone="primary"
            icon={<Paperclip size={11} />}
            onClick={onAttachContext}
          />
        )}
      </div>
    </footer>
  );
}

function ActionChip({
  label,
  tone,
  icon,
  href,
  onClick,
}: {
  label: string;
  tone: 'primary' | 'secondary';
  icon?: React.ReactNode;
  href?: string;
  onClick?: () => void;
}) {
  const className = cn(
    'inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium leading-none',
    'transition-colors whitespace-nowrap',
    tone === 'primary'
      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
      : 'border border-border bg-background/40 text-foreground hover:bg-muted/60'
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {icon}
        {label}
        <ExternalLink size={10} className="opacity-70" />
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className}>
      {icon}
      {label}
    </button>
  );
}

// ─── Multiple / Unsafe / None / NoIntegration ─────────────────────────────
//
// These branches keep the previous compact-card treatment — they're empty /
// fallback states, not the main "patient cockpit" experience the redesign
// targets. `CardShell` below has been tuned down to a quieter label style so
// the visual feel stays consistent with the new header / body / footer.
//
// TODO(rtm-redesign): once we have product clarity on multi-match UX, fold
// the picker into the same identity-summary treatment used in the header.

function CardShell({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-muted/20 px-3 py-2">
      <header className="flex items-center gap-1.5 mb-1.5">
        {icon}
        <h3 className="text-[12px] font-medium text-muted-foreground">{title}</h3>
      </header>
      {children}
    </section>
  );
}

/**
 * Interactive multi-match picker. Renders one clickable row per
 * candidate; clicking calls `/real-time-mode/resolve` again with
 * `confirmedEnterPatientId` set, which the gateway treats as a `matched`
 * state with the chosen patient's data.
 *
 * Privacy: candidate rows show identity-only (name, DOB, MRN) — never
 * financial data, since at this point we haven't authoritatively
 * disambiguated who the user is looking at.
 */
function MultiMatchPicker({
  context,
  candidates,
}: {
  context: import('@ctrl-enter/types').ObservedContext;
  candidates: EnterPatient[];
}) {
  const { confirmCandidate, isPending } = useResolveRealTimeModeMutation();
  const pickedCandidate = useRealTimeModePickedCandidate();

  return (
    <CardShell
      title="Multiple matches — pick the patient"
      icon={<Info size={11} className="text-muted-foreground" />}
    >
      {candidates.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">
          The match was ambiguous. Verify the patient manually in Enter.
        </p>
      ) : (
        <ul className="space-y-1">
          {candidates.map(candidate => {
            const isPickedNow = pickedCandidate?.enterPatientId === candidate.enterPatientId;
            const disabled = isPending;
            return (
              <li key={candidate.enterPatientId}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    void confirmCandidate(context, candidate);
                  }}
                  className={cn(
                    'w-full text-left text-[11px] flex flex-wrap items-baseline gap-x-2',
                    'px-2 py-1.5 rounded-md border border-transparent',
                    'hover:bg-muted/60 hover:border-border',
                    'transition-colors',
                    isPickedNow && 'border-primary/40 bg-primary/10',
                    disabled && !isPickedNow && 'opacity-60 cursor-not-allowed',
                    isPending && isPickedNow && 'cursor-wait'
                  )}
                >
                  {isPending && isPickedNow && (
                    <span className="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
                  )}
                  <span className="font-medium text-foreground">{candidate.name}</span>
                  {candidate.dob && (
                    <span className="text-muted-foreground">
                      DOB {formatDob(candidate.dob)}
                    </span>
                  )}
                  {candidate.mrn && (
                    <span className="text-muted-foreground">MRN {candidate.mrn}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </CardShell>
  );
}

function CandidateList({
  title,
  candidates,
}: {
  title: string;
  candidates: EnterPatient[];
}) {
  return (
    <CardShell title={title} icon={<User size={11} className="text-muted-foreground" />}>
      {candidates.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">
          No specific candidates returned. Please verify the patient manually in Enter.
        </p>
      ) : (
        <ul className="space-y-1">
          {candidates.map(candidate => (
            <li
              key={candidate.enterPatientId}
              className="text-[11px] flex flex-wrap items-baseline gap-x-2"
            >
              <span className="font-medium text-foreground">{candidate.name}</span>
              {candidate.dob && (
                <span className="text-muted-foreground">DOB {formatDob(candidate.dob)}</span>
              )}
              {candidate.mrn && (
                <span className="text-muted-foreground">MRN {candidate.mrn}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
}

function UnsafeMatch({
  candidates,
  observedNameOnly,
}: {
  candidates: EnterPatient[];
  observedNameOnly: boolean;
}) {
  return (
    <CardShell
      title="Match too ambiguous"
      icon={<AlertTriangle size={11} className="text-amber-500" />}
    >
      <p className="text-[11px] text-muted-foreground mb-2">
        {observedNameOnly
          ? 'Only a patient name was detected. Financial details are hidden until the match is confirmed.'
          : 'Match confidence is too low to display financial details automatically.'}
      </p>
      {candidates.length > 0 && (
        <CandidateList title="Possible matches" candidates={candidates} />
      )}
    </CardShell>
  );
}

function NoIntegration({
  onConnectIntegration,
}: {
  onConnectIntegration?: () => void;
}) {
  return (
    <CardShell title="Connect Enter" icon={<Plug size={11} className="text-primary" />}>
      <p className="text-[11px] text-muted-foreground mb-2">
        Real-Time Mode reads patient data from your clinic's Enter account. Connect Enter once and
        it'll auto-populate this panel whenever you open a ModMed chart.
      </p>
      {onConnectIntegration ? (
        <button
          type="button"
          onClick={onConnectIntegration}
          className={cn(
            'inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md',
            'bg-primary text-primary-foreground hover:bg-primary/90 transition-colors'
          )}
        >
          <Plug size={11} />
          Connect Enter
        </button>
      ) : (
        <a
          href="/?view=settings"
          target="_self"
          className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline underline-offset-2"
        >
          Open Integrations settings
          <ExternalLink size={10} />
        </a>
      )}
    </CardShell>
  );
}

function NoMatch({
  context,
}: {
  context: import('@ctrl-enter/types').ObservedContext;
}) {
  const f = context.observedFields;
  const searchedParts: string[] = [];
  if (f.mrn) searchedParts.push(`MRN ${f.mrn}`);
  if (f.dob) searchedParts.push(`DOB ${f.dob}`);
  if (f.firstName ?? f.lastName)
    searchedParts.push([f.firstName, f.lastName].filter(Boolean).join(' '));

  return (
    <CardShell title="No match" icon={<Info size={11} className="text-muted-foreground" />}>
      <p className="text-[11px] text-muted-foreground mb-1">
        No matching patient found in Enter for the observed context.
      </p>
      {searchedParts.length > 0 && (
        <p className="text-[11px] text-muted-foreground/70 mb-2 font-mono select-all">
          Searched: {searchedParts.join(' · ')}
        </p>
      )}
      <a
        href="https://platform.enter.health/search"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline underline-offset-2"
      >
        Search Enter manually
        <ExternalLink size={10} />
      </a>
    </CardShell>
  );
}

// ─── Formatting helpers ───────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDob(iso: string): string {
  // ISO YYYY-MM-DD → MM/DD/YYYY for US clinical contexts.
  const [year, month, day] = iso.split('-');
  if (!year || !month || !day) return iso;
  return `${month}/${day}/${year}`;
}

function formatDate(iso: string): string {
  return formatDob(iso);
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}
